import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { KeyRound, Loader2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-zinc-100 placeholder-zinc-500 outline-none transition-colors focus:border-neon-purple/60 focus:ring-1 focus:ring-neon-purple/40";

export default function MemberModal({ open, onClose, onVerified }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/membership/request", { email });
      setStep("code");
      toast.success(data.email_sent ? "Code sent — check your inbox." : "Code generated (email unavailable).");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/membership/verify", { email, code });
      localStorage.setItem("ad-member-token", data.token);
      toast.success("You're verified. Welcome to the velvet side.");
      onVerified?.();
      close();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setStep("email");
    setCode("");
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={close} data-testid="member-modal">
          <motion.div initial={{ y: 60, opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl border border-neon-purple/25 bg-[#0E1117] p-6 sm:p-8">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-neon-purple">Members Only</div>
                <h3 className="mt-2 font-display text-xl font-bold uppercase tracking-tight">
                  {step === "email" ? "Unlock the Velvet List" : "Enter Your Code"}
                </h3>
                <p className="mt-1 text-sm text-zinc-500">
                  {step === "email"
                    ? "Verify your email once to reveal venues for members-only nights."
                    : `We sent a 6-digit code to ${email}.`}
                </p>
              </div>
              <button onClick={close} data-testid="member-modal-close"
                className="rounded-full border border-white/10 p-2 text-zinc-400 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>

            {step === "email" ? (
              <form onSubmit={requestCode} className="space-y-4">
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email" data-testid="member-email-input" className={inputCls} />
                <button type="submit" disabled={loading} data-testid="member-request-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-neon-purple py-3.5 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-0.5 disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                  {loading ? "Sending code" : "Email Me a Code"}
                </button>
              </form>
            ) : (
              <form onSubmit={verify} className="space-y-4">
                <input required value={code} onChange={(e) => setCode(e.target.value)} maxLength={6}
                  placeholder="6-digit code" data-testid="member-code-input"
                  className={`${inputCls} text-center font-mono text-2xl tracking-[0.5em]`} />
                <button type="submit" disabled={loading} data-testid="member-verify-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-neon-purple py-3.5 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-0.5 disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  {loading ? "Verifying" : "Verify & Unlock"}
                </button>
                <button type="button" onClick={() => setStep("email")} data-testid="member-back-button"
                  className="w-full text-center font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-600 hover:text-zinc-300">
                  ← Use a different email
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
